import type { FunnelPageItem, FunnelPageRouting } from '../types';
import { DRAFT_FUNNEL_PAGE_ID } from '../constants/funnels.constants';
import { findRoutingCycles, getReachablePageIds } from './funnelGraph';

export interface RoutingWarning {
    id: string;
    kind: 'cycle' | 'orphan';
    message: string;
}

/**
 * Simulates applying `nextRouting` to the editing page and returns soft
 * warnings for cycles + orphaned pages. The draft slot is excluded from
 * the simulation since it isn't persisted yet.
 */
export function collectRoutingWarnings(
    allPages: FunnelPageItem[],
    editingPageId: number,
    nextRouting: FunnelPageRouting | null
): RoutingWarning[] {
    const simulated = allPages
        .filter(p => p.page_id !== DRAFT_FUNNEL_PAGE_ID)
        .map(p => (p.page_id === editingPageId ? { ...p, routing: nextRouting } : p));
    if (simulated.length === 0) return [];

    const out: RoutingWarning[] = [];

    findRoutingCycles(simulated).forEach(cycle => {
        const steps = cycle.map(id => simulated.find(p => p.page_id === id)?.order).filter(o => o !== undefined);
        out.push({
            id: `cycle-${cycle.join('-')}`,
            kind: 'cycle',
            message: `Step ${steps.join(' → Step ')} would loop back on itself.`,
        });
    });

    const reachable = getReachablePageIds(simulated);
    simulated.forEach(p => {
        if (!reachable.has(p.page_id)) {
            out.push({
                id: `orphan-${p.page_id}`,
                kind: 'orphan',
                message: `Step ${p.order} would no longer be reachable from the first page.`,
            });
        }
    });

    return out;
}
